import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/meta";
import {
  LayoutDashboard, ClipboardList, PlusCircle, PackageCheck, Boxes,
  Package, Building2, AlertTriangle, LogOut, Menu, X, Zap, BarChart3, Undo2, Users,
  UserCircle, ClipboardCheck, Hash, ShieldAlert, ChevronDown, MoreHorizontal,
  Tags, Truck, ShoppingCart, KeyRound, PackageMinus, QrCode, Shuffle, ListChecks, LineChart, ShieldCheck,
  Factory, CalendarDays, BookOpen, Wheat,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

const ALL = ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA", "UNIDADE"];
const PROD = ["GESTOR", "SUPERVISOR", "ESTOQUISTA"];

const TOP_LINKS = [
  { to: "/", label: "Visão Geral", icon: LayoutDashboard, roles: ALL },
  { to: "/qr", label: "Ler QR", icon: QrCode, roles: [...ALL, "TRANSPORTE"] },
];

const GROUPS = [
  { title: "Gestão", items: [
    { to: "/gestao/consumo", label: "Consumo & Abastecimento", icon: LineChart, roles: ["ADMIN", "GESTOR", "SUPERVISOR"] },
    { to: "/gestao/permissoes", label: "Matriz de Permissões", icon: ShieldCheck, roles: ["ADMIN", "GESTOR", "SUPERVISOR"] },
  ]},
  { title: "Operação", items: [
    { to: "/pedidos/novo", label: "Novo Pedido", icon: PlusCircle, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "UNIDADE"] },
    { to: "/pedidos", label: "Pedidos", icon: ClipboardList, roles: ALL },
    { to: "/recebimento", label: "Recebimentos", icon: PackageCheck, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "UNIDADE"] },
    { to: "/cargas", label: "Cargas / Transporte", icon: Truck, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA", "TRANSPORTE"] },
    { to: "/devolucoes", label: "Devoluções", icon: Undo2, roles: ALL },
    { to: "/ocorrencias", label: "Ocorrências", icon: AlertTriangle, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA"] },
  ]},
  { title: "Produção", items: [
    { to: "/producao", label: "Painel da Produção", icon: Factory, roles: PROD },
    { to: "/producao/planejamento", label: "Planejamento / Ordens", icon: CalendarDays, roles: PROD },
    { to: "/producao/compras", label: "Compras da Semana", icon: ShoppingCart, roles: PROD },
    { to: "/producao/requisicoes", label: "Requisições de Materiais", icon: ClipboardList, roles: PROD },
    { to: "/producao/estoque", label: "Estoque da Produção", icon: Boxes, roles: PROD },
    { to: "/producao/fichas", label: "Fichas Técnicas", icon: BookOpen, roles: PROD },
    { to: "/produtos?filtro=producao", label: "Materiais e Produtos", icon: Wheat, roles: PROD },
    { to: "/producao/relatorios", label: "Relatórios da Produção", icon: BarChart3, roles: PROD },
  ]},
  { title: "Estoque", items: [
    { to: "/estoque", label: "Estoque", icon: Boxes, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA"] },
    { to: "/estoque/saida", label: "Registrar Saída", icon: PackageMinus, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA"] },
    { to: "/compras", label: "Entrada / Pedido de Compra", icon: ShoppingCart, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA"] },
    { to: "/inventarios", label: "Inventários", icon: ClipboardCheck, roles: ["GESTOR", "SUPERVISOR", "ESTOQUISTA"] },
  ]},
  { title: "Cadastros", items: [
    { to: "/produtos", label: "Produtos", icon: Package, roles: ["ADMIN", "GESTOR", "SUPERVISOR"] },
    { to: "/grupos-substituicao", label: "Grupos de Substituição", icon: Shuffle, roles: ["ADMIN", "GESTOR", "SUPERVISOR"] },
    { to: "/motivos", label: "Motivos", icon: ListChecks, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "MASTER"] },
    { to: "/categorias", label: "Categorias", icon: Tags, roles: ["ADMIN", "GESTOR", "SUPERVISOR"] },
    { to: "/fornecedores", label: "Fornecedores", icon: Truck, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA"] },
    { to: "/veiculos", label: "Veículos (Frota)", icon: Truck, roles: ["ADMIN", "GESTOR", "SUPERVISOR"] },
    { to: "/unidades", label: "Unidades", icon: Building2, roles: ["GESTOR"] },
    { to: "/usuarios", label: "Usuários", icon: Users, roles: ["MASTER"] },
  ]},
  { title: "Relatórios", items: [
    { to: "/relatorios", label: "Relatórios", icon: BarChart3, roles: ["ADMIN", "GESTOR", "SUPERVISOR"] },
    { to: "/relatorios/saidas", label: "Saídas / Consumo", icon: PackageMinus, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA"] },
  ]},
  { title: "Configurações", items: [
    { to: "/config/empresa", label: "Empresa", icon: Building2, roles: ["MASTER"] },
    { to: "/config/numeracao", label: "Numeração", icon: Hash, roles: ["MASTER"] },
    { to: "/config/reset", label: "Reset & Manutenção", icon: ShieldAlert, roles: ["MASTER"], superOnly: true },
  ]},
  { title: "Minha Conta", items: [
    { to: "/minha-conta", label: "Minha Conta", icon: UserCircle, roles: ALL },
  ]},
];

const ALL_PATHS = [...TOP_LINKS.map((i) => i.to), ...GROUPS.flatMap((g) => g.items.map((i) => i.to))];

// Item de menu ativo = correspondência mais específica (evita irmão pai ficar destacado junto).
function bestMatch(pathname) {
  let best = null;
  for (const p of ALL_PATHS) {
    if (p === "/") continue;
    if (pathname === p || pathname.startsWith(p + "/")) {
      if (!best || p.length > best.length) best = p;
    }
  }
  return best;
}

const PRIMARY = [
  { to: "/", label: "Painel", icon: LayoutDashboard, roles: ALL },
  { to: "/cargas", label: "Cargas", icon: Truck, roles: ["TRANSPORTE"] },
  { to: "/qr", label: "Ler QR", icon: QrCode, roles: ["TRANSPORTE"] },
  { to: "/pedidos/novo", label: "Novo", icon: PlusCircle, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "UNIDADE"] },
  { to: "/pedidos", label: "Pedidos", icon: ClipboardList, roles: ALL },
  { to: "/recebimento", label: "Receber", icon: PackageCheck, roles: ["ADMIN", "GESTOR", "SUPERVISOR", "UNIDADE"] },
];

function TopLinks({ links, isActive, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 mb-3" data-testid="top-links">
      {links.map((n) => {
        const active = isActive(n.to);
        const Icon = n.icon;
        return (
          <Link key={n.to} to={n.to} onClick={onNavigate} data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
              active ? "bg-primary/15 text-primary border border-primary/40" : "text-foreground hover:bg-secondary border border-transparent"
            }`}>
            <Icon className="w-[18px] h-[18px]" /> {n.label}
          </Link>
        );
      })}
    </nav>
  );
}

function NavGroups({ groups, isActive, defaultCollapsed = false, onNavigate }) {
  const [collapsed, setCollapsed] = useState(
    () => Object.fromEntries(groups.map((g) => [g.title, defaultCollapsed]))
  );
  const toggle = (t) => setCollapsed((c) => ({ ...c, [t]: !c[t] }));
  return (
    <nav className="flex flex-col gap-2" data-testid="grouped-nav">
      {groups.map((g) => {
        const isCol = !!collapsed[g.title];
        return (
          <div key={g.title}>
            <button onClick={() => toggle(g.title)} data-testid={`nav-group-${g.title}`}
              className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground/70 hover:text-primary transition-colors">
              {g.title}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isCol ? "-rotate-90" : ""}`} />
            </button>
            {!isCol && (
              <div className="flex flex-col gap-1">
                {g.items.map((n) => {
                  const active = isActive(n.to);
                  const Icon = n.icon;
                  return (
                    <Link key={n.to} to={n.to} onClick={onNavigate} data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                        active ? "bg-primary/15 text-primary border border-primary/40" : "text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent"
                      }`}>
                      <Icon className="w-[18px] h-[18px]" /> {n.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
      <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center shrink-0 shadow-[0_0_18px_rgba(245,158,11,0.35)]">
        <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
      </div>
      <div className="leading-none">
        <div className="font-display text-xl font-extrabold uppercase tracking-tight text-foreground">NEXO<span className="text-primary">LOG</span></div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Uni Alimentos</div>
      </div>
    </Link>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isAdmin = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  const canSee = (it) => it.superOnly ? user.role === "SUPER_ADMIN" : (isAdmin || it.roles.includes(user.role));

  const visibleGroups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter(canSee) }))
    .filter((g) => g.items.length > 0);

  const isActive = (to) => {
    if (to === "/") return loc.pathname === "/";
    return bestMatch(loc.pathname) === to;
  };

  const primary = PRIMARY.filter(canSee);
  const topLinks = TOP_LINKS.filter(canSee);
  const bottomCols = primary.length + 1;

  return (
    <div className="min-h-screen grain flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card/60 backdrop-blur-xl fixed inset-y-0 p-4 z-20 pt-[calc(1rem+env(safe-area-inset-top))] pl-[calc(1rem+env(safe-area-inset-left))] pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div className="mb-5"><Brand /></div>
        <div className="flex-1 overflow-y-auto -mr-2 pr-2"><TopLinks links={topLinks} isActive={isActive} /><NavGroups groups={visibleGroups} isActive={isActive} defaultCollapsed={false} /></div>
        <div className="mt-auto pt-4 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2 px-1">Sessão</div>
          <div className="px-1 mb-3">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-xs text-primary uppercase tracking-wide">{ROLE_LABELS[user.role]}</div>
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={logout} data-testid="btn-logout">
            <LogOut className="w-4 h-4" /> Sair
          </Button>
          <div className="mt-3 text-center text-[10px] text-muted-foreground/60" data-testid="elite-signature-sidebar">Desenvolvido por Elite Tecnologia</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 relative z-10 flex flex-col min-h-screen min-w-0 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
        <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-xl overflow-hidden pt-[env(safe-area-inset-top)]">
          <div className="h-14 flex items-center justify-between gap-2 px-4">
            <div className="flex items-center gap-3 min-w-0">
              <button className="lg:hidden shrink-0" onClick={() => setMoreOpen(true)} data-testid="btn-open-menu"><Menu /></button>
              <div className="lg:hidden min-w-0 shrink"><Brand /></div>
            </div>
            <div className="flex items-center gap-2 min-w-0 shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 min-w-0 outline-none" data-testid="header-account">
                  <div className="flex flex-col items-end min-w-0">
                    <span className="text-sm font-semibold leading-none truncate max-w-[34vw]">{user.name}</span>
                    <span className="text-[10px] text-primary uppercase truncate max-w-[34vw]">{ROLE_LABELS[user.role]}</span>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-secondary border border-border flex items-center justify-center shrink-0"><UserCircle className="w-5 h-5 text-primary" /></div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="menu-minha-conta"><Link to="/minha-conta"><UserCircle className="w-4 h-4" /> Minha Conta</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-alterar-senha"><Link to="/minha-conta"><KeyRound className="w-4 h-4" /> Alterar senha</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-400" data-testid="menu-sair"><LogOut className="w-4 h-4" /> Sair</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 max-w-[1400px] w-full mx-auto overflow-x-hidden pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-6">{children}</main>

        {/* Bottom nav mobile */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 grid bg-card/95 backdrop-blur-xl border-t border-border pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
          style={{ gridTemplateColumns: `repeat(${bottomCols}, minmax(0, 1fr))` }} data-testid="bottom-nav">
          {primary.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`bottom-${n.to.replace(/\//g, "") || "home"}`}
                className={`flex flex-col items-center justify-center py-2 text-[10px] gap-0.5 ${active ? "text-primary" : "text-muted-foreground"}`}>
                <Icon className="w-5 h-5" /> {n.label}
              </Link>
            );
          })}
          <button onClick={() => setMoreOpen(true)} data-testid="bottom-mais"
            className="flex flex-col items-center justify-center py-2 text-[10px] gap-0.5 text-muted-foreground">
            <MoreHorizontal className="w-5 h-5" /> Mais
          </button>
        </nav>
      </div>

      {/* Menu completo mobile (Mais) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-sm p-0 flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]" data-testid="more-sheet">
          <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
          <div className="flex items-center justify-between p-4 border-b border-border">
            <Brand />
            <button onClick={() => setMoreOpen(false)} data-testid="more-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <TopLinks links={topLinks} isActive={isActive} onNavigate={() => setMoreOpen(false)} />
            <NavGroups groups={visibleGroups} isActive={isActive} defaultCollapsed onNavigate={() => setMoreOpen(false)} />
          </div>
          <div className="p-4 border-t border-border">
            <Button variant="outline" size="sm" className="w-full" onClick={logout}><LogOut className="w-4 h-4" /> Sair</Button>
            <div className="mt-3 text-center text-[10px] text-muted-foreground/60">Desenvolvido por Elite Tecnologia</div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
